import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Facebook, 
  Users, 
  FileText, 
  Clock,
  Plus,
  Trash2,
  Sparkles,
  Copy,
  ExternalLink,
  TrendingUp,
  Calendar,
  Target,
  Loader2,
  AlertCircle,
  CheckCircle,
  Database,
  Plane,
  Ship,
  Briefcase,
  PartyPopper,
  Palmtree,
  Car,
  Heart,
  ThumbsUp,
  MessageCircle,
  Share2,
  MousePointerClick,
  UserPlus,
  BarChart3,
  Edit
} from 'lucide-react';

const LEAD_CATEGORIES = {
  airport: { label: "Airport Transfers", icon: Plane, color: "bg-blue-500" },
  wedding: { label: "Weddings & Events", icon: Heart, color: "bg-pink-500" },
  corporate: { label: "Corporate/Business", icon: Briefcase, color: "bg-gray-600" },
  cruise: { label: "Cruise Port", icon: Ship, color: "bg-cyan-500" },
  event: { label: "Special Events", icon: PartyPopper, color: "bg-purple-500" },
  tourism: { label: "Tourism & Travel", icon: Palmtree, color: "bg-green-500" },
  general: { label: "General", icon: Car, color: "bg-gold-500" }
};

const AdminLeadManager = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [postLogs, setPostLogs] = useState([]);
  const [duePosts, setDuePosts] = useState([]);
  
  // UI states
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showEngagementDialog, setShowEngagementDialog] = useState(false);
  const [selectedPostLog, setSelectedPostLog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });
  const [generating, setGenerating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [engagementStats, setEngagementStats] = useState(null);
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    url: '',
    category: 'general',
    member_count: '',
    post_frequency: 'weekly',
    notes: ''
  });
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'general',
    content: '',
    hashtags: '',
    call_to_action: '',
    image_suggestion: ''
  });
  
  const [aiForm, setAiForm] = useState({
    category: 'general',
    event_name: '',
    tone: 'professional',
    include_pricing: false,
    special_offer: ''
  });
  
  const [logForm, setLogForm] = useState({
    content: '',
    template_id: ''
  });
  
  const [engagementForm, setEngagementForm] = useState({
    likes: 0,
    comments: 0,
    shares: 0,
    clicks: 0,
    leads_generated: 0,
    engagement_notes: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, groupsRes, templatesRes, logsRes, dueRes, engagementRes] = await Promise.all([
        axios.get(`${API}/admin/lead-gen/stats`),
        axios.get(`${API}/admin/lead-gen/groups`),
        axios.get(`${API}/admin/lead-gen/templates`),
        axios.get(`${API}/admin/lead-gen/post-logs`),
        axios.get(`${API}/admin/lead-gen/due-posts`),
        axios.get(`${API}/admin/lead-gen/engagement-stats`)
      ]);
      
      setStats(statsRes.data);
      setGroups(groupsRes.data);
      setTemplates(templatesRes.data);
      setPostLogs(logsRes.data);
      setDuePosts(dueRes.data);
      setEngagementStats(engagementRes.data);
    } catch (error) {
      toast.error('Failed to load lead generation data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedGroups = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/lead-gen/groups/seed`);
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to seed groups');
    } finally {
      setSeeding(false);
    }
  };

  const handleAddGroup = async () => {
    if (!groupForm.name || !groupForm.url || !groupForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/lead-gen/groups`, {
        ...groupForm,
        member_count: groupForm.member_count ? parseInt(groupForm.member_count) : null
      });
      toast.success('Group added successfully');
      setShowGroupDialog(false);
      setGroupForm({ name: '', url: '', category: 'general', member_count: '', post_frequency: 'weekly', notes: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add group');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await axios.delete(`${API}/admin/lead-gen/groups/${deleteDialog.id}`);
      toast.success('Group deleted');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleAddTemplate = async () => {
    if (!templateForm.name || !templateForm.content) {
      toast.error('Please fill in name and content');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/lead-gen/templates`, {
        ...templateForm,
        hashtags: templateForm.hashtags ? templateForm.hashtags.split(',').map(h => h.trim()) : []
      });
      toast.success('Template saved');
      setShowTemplateDialog(false);
      setTemplateForm({ name: '', category: 'general', content: '', hashtags: '', call_to_action: '', image_suggestion: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await axios.delete(`${API}/admin/lead-gen/templates/${deleteDialog.id}`);
      toast.success('Template deleted');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleGeneratePost = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/lead-gen/generate-post`, aiForm);
      setGeneratedPost(response.data);
      toast.success('Post generated!');
    } catch (error) {
      toast.error('Failed to generate post: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleLogPost = async () => {
    if (!selectedGroup || !logForm.content) {
      toast.error('Please select a group and enter post content');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/lead-gen/log-post`, null, {
        params: {
          group_id: selectedGroup.id,
          content: logForm.content,
          template_id: logForm.template_id || undefined
        }
      });
      toast.success('Post logged successfully');
      setShowLogDialog(false);
      setSelectedGroup(null);
      setLogForm({ content: '', template_id: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to log post');
    }
  };

  const handleUpdateEngagement = async () => {
    if (!selectedPostLog) return;
    
    try {
      await axios.put(`${API}/admin/lead-gen/post-logs/${selectedPostLog.id}`, engagementForm);
      toast.success('Engagement updated');
      setShowEngagementDialog(false);
      setSelectedPostLog(null);
      setEngagementForm({ likes: 0, comments: 0, shares: 0, clicks: 0, leads_generated: 0, engagement_notes: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update engagement');
    }
  };

  const openEngagementDialog = (log) => {
    setSelectedPostLog(log);
    setEngagementForm({
      likes: log.likes || 0,
      comments: log.comments || 0,
      shares: log.shares || 0,
      clicks: log.clicks || 0,
      leads_generated: log.leads_generated || 0,
      engagement_notes: log.engagement_notes || ''
    });
    setShowEngagementDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryInfo = (category) => {
    return LEAD_CATEGORIES[category] || LEAD_CATEGORIES.general;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-lead-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Facebook className="h-7 w-7 text-blue-600" />
            Lead Generation Manager
          </h1>
          <p className="text-gray-500 mt-1">Manage Facebook group marketing and generate leads</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeedGroups}
            disabled={seeding}
            data-testid="seed-groups-btn"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
            Seed Miami Groups
          </Button>
          <Button onClick={() => setShowAIDialog(true)} data-testid="ai-generate-btn">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Generate Post
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_groups}</p>
                  <p className="text-xs text-gray-500">Total Groups</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.active_groups}</p>
                  <p className="text-xs text-gray-500">Active Groups</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_posts}</p>
                  <p className="text-xs text-gray-500">Posts Made</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.posts_this_week}</p>
                  <p className="text-xs text-gray-500">This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-pink-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_templates}</p>
                  <p className="text-xs text-gray-500">Templates</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-500" />
                <div>
                  <p className="text-2xl font-bold">{formatNumber(stats.potential_reach)}</p>
                  <p className="text-xs text-gray-500">Potential Reach</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Due Posts Alert */}
      {duePosts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
              <AlertCircle className="h-5 w-5" />
              {duePosts.length} Groups Due for Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {duePosts.slice(0, 5).map(group => {
                const catInfo = getCategoryInfo(group.category);
                const CategoryIcon = catInfo.icon;
                return (
                  <Badge 
                    key={group.id} 
                    variant="outline" 
                    className="cursor-pointer hover:bg-white"
                    onClick={() => {
                      setSelectedGroup(group);
                      setShowLogDialog(true);
                    }}
                  >
                    <CategoryIcon className="h-3 w-3 mr-1" />
                    {group.name}
                    <span className="ml-1 text-orange-600">
                      ({group.days_since_post}d ago)
                    </span>
                  </Badge>
                );
              })}
              {duePosts.length > 5 && (
                <Badge variant="secondary">+{duePosts.length - 5} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Users className="h-4 w-4 mr-2" />
            Groups
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            Post History
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Groups Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Facebook Groups</h2>
            <Button onClick={() => setShowGroupDialog(true)} data-testid="add-group-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Group
            </Button>
          </div>
          
          {groups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Facebook className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No Facebook groups added yet</p>
                <Button onClick={handleSeedGroups} disabled={seeding}>
                  {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
                  Add Miami Groups
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {groups.map(group => {
                const catInfo = getCategoryInfo(group.category);
                const CategoryIcon = catInfo.icon;
                const isDue = duePosts.some(d => d.id === group.id);
                
                return (
                  <Card key={group.id} className={isDue ? 'border-orange-300' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${catInfo.color}`}>
                            <CategoryIcon className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-sm font-medium line-clamp-1">
                              {group.name}
                            </CardTitle>
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {catInfo.label}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => window.open(group.url, '_blank')}
                            data-testid={`open-group-${group.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => setDeleteDialog({ open: true, type: 'group', id: group.id, name: group.name })}
                            data-testid={`delete-group-${group.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-gray-500">
                          <span>Members:</span>
                          <span className="font-medium text-gray-900">{formatNumber(group.member_count)}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                          <span>Frequency:</span>
                          <span className="capitalize">{group.post_frequency}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                          <span>Last Posted:</span>
                          <span className={isDue ? 'text-orange-600 font-medium' : ''}>
                            {formatDate(group.last_posted)}
                          </span>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full mt-4"
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowLogDialog(true);
                        }}
                        data-testid={`log-post-${group.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Log a Post
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Post Templates</h2>
            <Button onClick={() => setShowTemplateDialog(true)} data-testid="add-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
          
          {templates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No templates created yet</p>
                <Button onClick={() => setShowTemplateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {templates.map(template => {
                const catInfo = getCategoryInfo(template.category);
                const CategoryIcon = catInfo.icon;
                
                return (
                  <Card key={template.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${catInfo.color}`}>
                            <CategoryIcon className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-sm">{template.name}</CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">{catInfo.label}</Badge>
                              <span className="text-xs text-gray-400">Used {template.use_count}x</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => copyToClipboard(template.content)}
                            data-testid={`copy-template-${template.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => setDeleteDialog({ open: true, type: 'template', id: template.id, name: template.name })}
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 line-clamp-3 mb-3">{template.content}</p>
                      {template.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {template.hashtags.map((tag, i) => (
                            <span key={i} className="text-xs text-blue-500">{tag}</span>
                          ))}
                        </div>
                      )}
                      {template.call_to_action && (
                        <p className="text-xs text-gray-500 mt-2 italic">CTA: {template.call_to_action}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <h2 className="text-lg font-semibold">Post History</h2>
          
          {postLogs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No posts logged yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {postLogs.map(log => (
                <Card key={log.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{log.group_name}</Badge>
                          <span className="text-xs text-gray-400">{formatDate(log.posted_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{log.content}</p>
                        
                        {/* Engagement Metrics */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1" title="Likes">
                            <ThumbsUp className="h-3 w-3" /> {log.likes || 0}
                          </span>
                          <span className="flex items-center gap-1" title="Comments">
                            <MessageCircle className="h-3 w-3" /> {log.comments || 0}
                          </span>
                          <span className="flex items-center gap-1" title="Shares">
                            <Share2 className="h-3 w-3" /> {log.shares || 0}
                          </span>
                          <span className="flex items-center gap-1" title="Clicks">
                            <MousePointerClick className="h-3 w-3" /> {log.clicks || 0}
                          </span>
                          <span className="flex items-center gap-1 text-green-600 font-medium" title="Leads Generated">
                            <UserPlus className="h-3 w-3" /> {log.leads_generated || 0} leads
                          </span>
                        </div>
                        
                        {log.engagement_notes && (
                          <p className="text-xs text-gray-400 mt-2 italic">Notes: {log.engagement_notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEngagementDialog(log)}
                          title="Update Engagement"
                          data-testid={`edit-engagement-${log.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyToClipboard(log.content)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <h2 className="text-lg font-semibold">Engagement Analytics</h2>
          
          {engagementStats && (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.total_likes}</p>
                        <p className="text-xs text-gray-500">Total Likes</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.total_comments}</p>
                        <p className="text-xs text-gray-500">Total Comments</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-cyan-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.total_shares}</p>
                        <p className="text-xs text-gray-500">Total Shares</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <MousePointerClick className="h-5 w-5 text-orange-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.total_clicks}</p>
                        <p className="text-xs text-gray-500">Total Clicks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.total_leads}</p>
                        <p className="text-xs text-gray-500">Leads Generated</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-pink-500" />
                      <div>
                        <p className="text-2xl font-bold">{engagementStats.avg_engagement}</p>
                        <p className="text-xs text-gray-500">Avg. Engagement</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Performing Groups */}
              {engagementStats.top_performing_groups?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5 text-green-500" />
                      Top Performing Groups
                    </CardTitle>
                    <CardDescription>Groups ranked by leads generated</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {engagementStats.top_performing_groups.map((group, index) => (
                        <div key={group.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-gold-400 text-gold-900' :
                              index === 1 ? 'bg-gray-300 text-gray-700' :
                              index === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {index + 1}
                            </span>
                            <span className="font-medium">{group.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-500">{group.posts} posts</span>
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="h-3 w-3" /> {group.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" /> {group.comments}
                            </span>
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <UserPlus className="h-3 w-3" /> {group.leads} leads
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {engagementStats.total_posts === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No engagement data yet</p>
                    <p className="text-sm text-gray-400 mt-2">Log posts and track their engagement to see analytics here</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Facebook Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={groupForm.name}
                onChange={e => setGroupForm({...groupForm, name: e.target.value})}
                placeholder="Miami Events & Activities"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <Label>Group URL *</Label>
              <Input
                value={groupForm.url}
                onChange={e => setGroupForm({...groupForm, url: e.target.value})}
                placeholder="https://facebook.com/groups/..."
                data-testid="group-url-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={groupForm.category} onValueChange={v => setGroupForm({...groupForm, category: v})}>
                  <SelectTrigger data-testid="group-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEAD_CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Members</Label>
                <Input
                  type="number"
                  value={groupForm.member_count}
                  onChange={e => setGroupForm({...groupForm, member_count: e.target.value})}
                  placeholder="50000"
                  data-testid="group-members-input"
                />
              </div>
            </div>
            <div>
              <Label>Post Frequency</Label>
              <Select value={groupForm.post_frequency} onValueChange={v => setGroupForm({...groupForm, post_frequency: v})}>
                <SelectTrigger data-testid="group-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={groupForm.notes}
                onChange={e => setGroupForm({...groupForm, notes: e.target.value})}
                placeholder="Any notes about this group..."
                rows={2}
                data-testid="group-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={handleAddGroup} data-testid="save-group-btn">Add Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Post Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={templateForm.name}
                onChange={e => setTemplateForm({...templateForm, name: e.target.value})}
                placeholder="Airport Transfer Promo"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={templateForm.category} onValueChange={v => setTemplateForm({...templateForm, category: v})}>
                <SelectTrigger data-testid="template-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LEAD_CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Post Content *</Label>
              <Textarea
                value={templateForm.content}
                onChange={e => setTemplateForm({...templateForm, content: e.target.value})}
                placeholder="Write your post content here..."
                rows={5}
                data-testid="template-content-input"
              />
            </div>
            <div>
              <Label>Hashtags (comma-separated)</Label>
              <Input
                value={templateForm.hashtags}
                onChange={e => setTemplateForm({...templateForm, hashtags: e.target.value})}
                placeholder="#Miami, #LuxuryTravel, #BlackCar"
                data-testid="template-hashtags-input"
              />
            </div>
            <div>
              <Label>Call to Action</Label>
              <Input
                value={templateForm.call_to_action}
                onChange={e => setTemplateForm({...templateForm, call_to_action: e.target.value})}
                placeholder="Book now at blackhdrive.com"
                data-testid="template-cta-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTemplate} data-testid="save-template-btn">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Post Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Post Generator
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Target Category</Label>
              <Select value={aiForm.category} onValueChange={v => setAiForm({...aiForm, category: v})}>
                <SelectTrigger data-testid="ai-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LEAD_CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tone</Label>
              <Select value={aiForm.tone} onValueChange={v => setAiForm({...aiForm, tone: v})}>
                <SelectTrigger data-testid="ai-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event Tie-in (optional)</Label>
              <Input
                value={aiForm.event_name}
                onChange={e => setAiForm({...aiForm, event_name: e.target.value})}
                placeholder="Art Basel, Super Bowl..."
                data-testid="ai-event-input"
              />
            </div>
            <div>
              <Label>Special Offer (optional)</Label>
              <Input
                value={aiForm.special_offer}
                onChange={e => setAiForm({...aiForm, special_offer: e.target.value})}
                placeholder="15% off first ride"
                data-testid="ai-offer-input"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="include-pricing"
                checked={aiForm.include_pricing}
                onChange={e => setAiForm({...aiForm, include_pricing: e.target.checked})}
                className="rounded"
                data-testid="ai-pricing-checkbox"
              />
              <Label htmlFor="include-pricing" className="cursor-pointer">Include pricing information</Label>
            </div>
          </div>
          
          <Button 
            onClick={handleGeneratePost} 
            disabled={generating}
            className="w-full"
            data-testid="generate-post-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Post
              </>
            )}
          </Button>
          
          {generatedPost && (
            <div className="mt-4 space-y-4">
              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Generated Post</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{generatedPost.content}</p>
                  {generatedPost.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {generatedPost.hashtags.map((tag, i) => (
                        <span key={i} className="text-xs text-blue-500">{tag}</span>
                      ))}
                    </div>
                  )}
                  {generatedPost.image_suggestion && (
                    <p className="text-xs text-gray-500 mt-3 italic">
                      Image suggestion: {generatedPost.image_suggestion}
                    </p>
                  )}
                </CardContent>
              </Card>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => copyToClipboard(generatedPost.content + '\n\n' + generatedPost.hashtags?.join(' '))}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setTemplateForm({
                      name: `AI: ${LEAD_CATEGORIES[aiForm.category]?.label || 'General'}`,
                      category: aiForm.category,
                      content: generatedPost.content,
                      hashtags: generatedPost.hashtags?.join(', ') || '',
                      call_to_action: '',
                      image_suggestion: generatedPost.image_suggestion || ''
                    });
                    setShowAIDialog(false);
                    setShowTemplateDialog(true);
                  }}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Save as Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Log Post Dialog */}
      <Dialog open={showLogDialog} onOpenChange={(open) => {
        setShowLogDialog(open);
        if (!open) {
          setSelectedGroup(null);
          setLogForm({ content: '', template_id: '' });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log a Post</DialogTitle>
            {selectedGroup && (
              <CardDescription>
                Logging post to: <span className="font-medium">{selectedGroup.name}</span>
              </CardDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            {templates.length > 0 && (
              <div>
                <Label>Use Template (optional)</Label>
                <Select 
                  value={logForm.template_id || "none"} 
                  onValueChange={v => {
                    const template = templates.find(t => t.id === v);
                    setLogForm({
                      ...logForm, 
                      template_id: v === "none" ? "" : v,
                      content: template ? template.content : logForm.content
                    });
                  }}
                >
                  <SelectTrigger data-testid="log-template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Post Content *</Label>
              <Textarea
                value={logForm.content}
                onChange={e => setLogForm({...logForm, content: e.target.value})}
                placeholder="Paste your post content here..."
                rows={6}
                data-testid="log-content-input"
              />
            </div>
            {selectedGroup?.url && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(selectedGroup.url, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Group in New Tab
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogDialog(false)}>Cancel</Button>
            <Button onClick={handleLogPost} data-testid="log-post-confirm-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Log Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Engagement Dialog */}
      <Dialog open={showEngagementDialog} onOpenChange={(open) => {
        setShowEngagementDialog(open);
        if (!open) {
          setSelectedPostLog(null);
          setEngagementForm({ likes: 0, comments: 0, shares: 0, clicks: 0, leads_generated: 0, engagement_notes: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Update Engagement
            </DialogTitle>
            {selectedPostLog && (
              <CardDescription>
                Post to: <span className="font-medium">{selectedPostLog.group_name}</span>
              </CardDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3" /> Likes
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={engagementForm.likes}
                  onChange={e => setEngagementForm({...engagementForm, likes: parseInt(e.target.value) || 0})}
                  data-testid="engagement-likes-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" /> Comments
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={engagementForm.comments}
                  onChange={e => setEngagementForm({...engagementForm, comments: parseInt(e.target.value) || 0})}
                  data-testid="engagement-comments-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Share2 className="h-3 w-3" /> Shares
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={engagementForm.shares}
                  onChange={e => setEngagementForm({...engagementForm, shares: parseInt(e.target.value) || 0})}
                  data-testid="engagement-shares-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <MousePointerClick className="h-3 w-3" /> Clicks
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={engagementForm.clicks}
                  onChange={e => setEngagementForm({...engagementForm, clicks: parseInt(e.target.value) || 0})}
                  data-testid="engagement-clicks-input"
                />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-green-600">
                <UserPlus className="h-3 w-3" /> Leads Generated
              </Label>
              <Input
                type="number"
                min="0"
                value={engagementForm.leads_generated}
                onChange={e => setEngagementForm({...engagementForm, leads_generated: parseInt(e.target.value) || 0})}
                data-testid="engagement-leads-input"
                className="border-green-200 focus:border-green-500"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={engagementForm.engagement_notes}
                onChange={e => setEngagementForm({...engagementForm, engagement_notes: e.target.value})}
                placeholder="Any notes about this post's performance..."
                rows={2}
                data-testid="engagement-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEngagementDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateEngagement} data-testid="save-engagement-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Engagement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type === 'group' ? 'Group' : 'Template'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => deleteDialog.type === 'group' ? handleDeleteGroup() : handleDeleteTemplate()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminLeadManager;
